import { isAshcorpBoHost } from 'app/modules/services-store/services-host.util';

/** Sellable module codes → Fuse navigation items (tenant routes at app root). */
import { buildCollapsableNavForModule } from 'app/shared/module-ui/module-ui-nav';
export type ModuleNavItem = {
    id: string;
    title: string;
    type: 'basic' | 'collapsable';
    icon: string;
    link?: string;
    children?: ModuleNavItem[];
};

const MODULE_UI_COLLAPSABLE_CODES = new Set([
    'hrm', 'accounting', 'payroll', 'pms', 'billing', 'website', 'marketing', 'wallet', 'ai',
]);

const MODULE_NAV: Record<string, ModuleNavItem | ModuleNavItem[]> = {
    dms: {
        id: 'dms',
        title: 'Documents & files',
        type: 'collapsable',
        icon: 'heroicons_outline:document-text',
        children: [
            { id: 'dms.files', title: 'Files', type: 'basic', icon: 'heroicons_outline:folder-open', link: '/dms/files' },
            { id: 'dms.documents', title: 'Documents', type: 'basic', icon: 'heroicons_outline:document-text', link: '/dms/documents' },
            { id: 'dms.templates', title: 'Templates', type: 'basic', icon: 'heroicons_outline:clipboard-list', link: '/dms/templates' },
            { id: 'dms.contracts', title: 'Contracts', type: 'basic', icon: 'heroicons_outline:document-duplicate', link: '/dms/contracts' },
            { id: 'dms.letterheads', title: 'Letterheads', type: 'basic', icon: 'heroicons_outline:document', link: '/dms/letterheads' },
            { id: 'dms.settings', title: 'Settings', type: 'basic', icon: 'heroicons_outline:cog', link: '/dms/settings' },
        ],
    },
    store: isAshcorpBoHost()
        ? {
              id: 'store',
              title: 'Store',
              type: 'collapsable',
              icon: 'heroicons_outline:shopping-bag',
              children: [
                  { id: 'store.settings', title: 'Settings', type: 'basic', icon: 'heroicons_outline:cog', link: '/store/admin/settings' },
                  { id: 'store.catalog', title: 'Catalog', type: 'basic', icon: 'heroicons_outline:tag', link: '/store/admin/catalog' },
                  { id: 'store.orders', title: 'Orders', type: 'basic', icon: 'heroicons_outline:clipboard-list', link: '/store/admin/orders' },
              ],
          }
        : {
              id: 'store',
              title: 'Store',
              type: 'collapsable',
              icon: 'heroicons_outline:shopping-bag',
              children: [
                  { id: 'store.overview', title: 'Overview', type: 'basic', icon: 'heroicons_outline:home', link: '/store' },
                  { id: 'store.orders', title: 'Orders', type: 'basic', icon: 'heroicons_outline:clipboard-list', link: '/store/orders' },
                  { id: 'store.pos', title: 'POS', type: 'basic', icon: 'heroicons_outline:credit-card', link: '/store/pos' },
              ],
          },
    mailing: {
        id: 'mailing',
        title: 'Mailing',
        type: 'collapsable',
        icon: 'heroicons_outline:mail',
        children: [
            { id: 'mailing.compose', title: 'Compose', type: 'basic', icon: 'heroicons_outline:pencil-alt', link: '/mailing/compose' },
            { id: 'mailing.drafts', title: 'Drafts', type: 'basic', icon: 'heroicons_outline:inbox', link: '/mailing/drafts' },
            { id: 'mailing.review', title: 'Review queue', type: 'basic', icon: 'heroicons_outline:clipboard-check', link: '/mailing/review' },
            { id: 'mailing.mailboxes', title: 'Mailboxes', type: 'basic', icon: 'heroicons_outline:inbox-in', link: '/mailing/mailboxes' },
            { id: 'mailing.settings', title: 'Settings', type: 'basic', icon: 'heroicons_outline:cog', link: '/mailing/settings' },
        ],
    },

    support: {
        id: 'support',
        title: 'Support',
        type: 'collapsable',
        icon: 'heroicons_outline:support',
        children: [
            { id: 'support.tickets', title: 'Tickets', type: 'basic', icon: 'heroicons_outline:ticket', link: '/support' },
            { id: 'support.settings', title: 'Settings', type: 'basic', icon: 'heroicons_outline:cog', link: '/support/settings' },
        ],
    },
};

export function buildModuleNavigationItems(moduleCodes: string[] | undefined | null): ModuleNavItem[] {
    if (!Array.isArray(moduleCodes) || moduleCodes.length === 0) {
        return [];
    }
    const items: ModuleNavItem[] = [];
    for (const code of sortModuleCodes(moduleCodes)) {
        if (MODULE_UI_COLLAPSABLE_CODES.has(code)) {
            items.push(buildCollapsableNavForModule(code));
            continue;
        }
        const entry = MODULE_NAV[code];
        if (!entry) {
            continue;
        }
        if (Array.isArray(entry)) {
            items.push(...entry);
        } else {
            items.push(entry);
        }
    }
    return items;
}

export function isModuleActive(moduleCode: string, moduleCodes: string[] | undefined | null): boolean {
    return Array.isArray(moduleCodes) && moduleCodes.includes(moduleCode);
}

/** Production branch: validated tenant modules only (display order). */
export const UI_PREVIEW_MODULE_CODES = ['store', 'website'];

const MODULE_NAV_DISPLAY_ORDER: Record<string, number> = {
    store: 10,
    website: 20,
};

function sortModuleCodes(codes: string[]): string[] {
    return [...codes].sort(
        (a, b) => (MODULE_NAV_DISPLAY_ORDER[a] ?? 100) - (MODULE_NAV_DISPLAY_ORDER[b] ?? 100)
    );
}
